import React, { useState, useEffect, lazy, Suspense } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
} from "react-router-dom";
import { connect } from "react-redux";
import ThemeProviderWrapper from "./ThemeProviderWrapper";
import { themes } from "./themes";
import { ResponsiveProvider } from "./contexts/ResponsiveContext";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import Header from "./components/common/Header/Header";
import Footer from "./components/common/Footer/Footer";
import { Container, CircularProgress } from "@mui/material";
import CssBaseline from "@mui/material/CssBaseline";
import { getLibraryCardRequest } from "./actions/libraryCardActions";
import { initGA, trackPageView } from "./utils/analytics";

// Lazy-loaded components
const Home = lazy(() => import("./components/pages/Home/Home"));
const Solutions = lazy(() => import("./components/pages/Solutions/Solutions"));
const Pricing = lazy(() => import("./components/pages/Pricing/Pricing"));
const Contact = lazy(() => import("./components/pages/Contact/Contact"));
const About = lazy(() => import("./components/pages/About/About"));
const UseCases = lazy(() => import("./components/pages/UseCases/UseCases"));
const Platform = lazy(() => import("./components/pages/Platform/Platform"));
const AIAgents = lazy(() => import("./components/pages/AIAgents/AIAgents"));
const AIAgentsBusiness = lazy(() =>
  import("./components/pages/AIAgentsBusiness/AIAgentsBusiness")
);
const AIAgentsComparison = lazy(() =>
  import("./components/pages/AIAgentsComparison/AIAgentsComparison")
);
const AgentArchitectureTypes = lazy(() =>
  import("./components/pages/AgentArchitectureTypes/AgentArchitectureTypes")
);
const AgentInteractionPatterns = lazy(() =>
  import("./components/pages/AgentInteractionPatterns/AgentInteractionPatterns")
);
const AIAutomationStudio = lazy(() =>
  import("./components/pages/AIAutomationStudio/AIAutomationStudio")
);
const Startups = lazy(() => import("./components/pages/Startups/Startups"));
const FipsrAIJourney = lazy(() =>
  import("./components/pages/FipsrAIJourney/FipsrAIJourney")
);
const FounderProfile = lazy(() =>
  import("./components/pages/FounderProfile/FounderProfile")
);
const Infrastructure = lazy(() =>
  import("./components/pages/Infrastructure/Infrastructure")
);
const MultiAgentArchitectures = lazy(() =>
  import("./components/pages/MultiAgentArchitectures/MultiAgentArchitectures")
);
const RAGSystemsArchitecture = lazy(() =>
  import("./components/pages/RAGSystemsArchitecture/RAGSystemsArchitecture")
);
const APIKeyRegistration = lazy(() =>
  import("./components/pages/APIKeyRegistration/APIKeyRegistration")
);
const AIAgentConfiguration = lazy(() =>
  import("./components/pages/AIAgentConfiguration/AIAgentConfiguration")
);
const ThemeDesigner = lazy(() => import("./components/ThemeDesigner"));

// Loading component
const Loading = () => (
  <Container className="flex justify-center items-center min-h-screen">
    <CircularProgress />
  </Container>
);

// Component to track route changes and scroll to top
const RouteTracker = () => {
  const location = useLocation();

  useEffect(() => {
    // Scroll to top on route change
    window.scrollTo(0, 0);
    // Track page view on route change
    trackPageView(location.pathname + location.search);
  }, [location]);

  return null;
};

const App = ({ cardDetails, getLibraryCardRequest }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [theme, setTheme] = useState(themes.teal); // Default theme is teal
  const [themeLoaded, setThemeLoaded] = useState(false);

  // Initialize Google Analytics
  useEffect(() => {
    const measurementId = process.env.REACT_APP_GA_MEASUREMENT_ID;
    if (measurementId) {
      initGA(measurementId);
    } else {
      console.warn(
        "Google Analytics Measurement ID not found. Set REACT_APP_GA_MEASUREMENT_ID in your .env file."
      );
    }
  }, []);

  // Load theme from localStorage first (for fast initial load)
  useEffect(() => {
    const token = localStorage.getItem("jwtToken");
    if (token) {
      setIsAuthenticated(true);
    }
    const savedTheme = localStorage.getItem("THEME");
    if (savedTheme) {
      try {
        const themeData = JSON.parse(savedTheme);
        setTheme(themeData);
        setThemeLoaded(true);
      } catch (e) {
        console.error("Error parsing saved theme:", e);
        setTheme(themes.teal);
        setThemeLoaded(true);
      }
    } else {
      setThemeLoaded(true);
    }
  }, []);

  // Always load theme from database (to get latest selection) - for all users
  useEffect(() => {
    const TENANT_ID = "fipsr";
    const libraryCardPayload = {
      operationType: "GET_LIBRARY_CARD",
      tenantId: TENANT_ID,
      entityType: "THEMESET",
      entityId: TENANT_ID,
    };
    console.log("Making database call to fetch theme data...");
    getLibraryCardRequest(libraryCardPayload);
  }, [getLibraryCardRequest]);

  // Update theme when database data is loaded and compare with localStorage
  useEffect(() => {
    console.log("cardDetails changed:", cardDetails);

    if (cardDetails === null) {
      // No theme set found in database, use localStorage theme or default
      console.log(
        "No theme set found in database, using localStorage or default theme"
      );
      return;
    }

    if (
      cardDetails &&
      typeof cardDetails === "string" &&
      cardDetails.length > 0
    ) {
      try {
        const cardDetailsObj = JSON.parse(cardDetails);
        if (cardDetailsObj && cardDetailsObj?.themeSet) {
          const loadedThemes = cardDetailsObj.themeSet;
          const selectedTheme = loadedThemes.find((theme) => theme.isSelected);

          if (selectedTheme) {
            console.log(
              "Found selected theme in database:",
              selectedTheme.themeName
            );

            // Get current theme from localStorage for comparison
            const currentLocalStorageTheme = localStorage.getItem("THEME");
            let shouldUpdateTheme = true;

            if (currentLocalStorageTheme) {
              try {
                const localTheme = JSON.parse(currentLocalStorageTheme);
                console.log(
                  "Current localStorage theme:",
                  localTheme.themeName
                );
                console.log("Database theme:", selectedTheme.themeName);

                // Compare theme names to see if they're different
                if (localTheme.themeName === selectedTheme.themeName) {
                  shouldUpdateTheme = false;
                  console.log("Themes are the same, no update needed");
                } else {
                  console.log("Themes are different, update needed");
                }
              } catch (e) {
                console.error(
                  "Error parsing localStorage theme for comparison:",
                  e
                );
              }
            } else {
              console.log("No localStorage theme found, will update");
            }

            if (shouldUpdateTheme) {
              console.log(
                "Updating theme from database:",
                selectedTheme.themeName
              );
              console.log(
                "Previous theme was:",
                currentLocalStorageTheme
                  ? JSON.parse(currentLocalStorageTheme).themeName
                  : "none"
              );
              setTheme(selectedTheme);
              localStorage.setItem("THEME", JSON.stringify(selectedTheme));
            } else {
              console.log(
                "Theme is already up to date:",
                selectedTheme.themeName
              );
            }
          }
        }
      } catch (error) {
        console.error("Error parsing theme data from database:", error);
      }
    }
  }, [cardDetails]);

  const handleThemeChange = (newTheme) => {
    setTheme(newTheme);
    localStorage.setItem("THEME", JSON.stringify(newTheme));
  };

  return (
    <Router>
      <ResponsiveProvider>
        <ThemeProviderWrapper theme={theme}>
          <DndProvider backend={HTML5Backend}>
            <CssBaseline />
            <RouteTracker />
            <Header
              isAuthenticated={isAuthenticated}
              setIsAuthenticated={setIsAuthenticated}
            />
            <Suspense fallback={<Loading />}>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/ai-agents" element={<AIAgents />} />
                <Route
                  path="/ai-agents-business"
                  element={<AIAgentsBusiness />}
                />
                <Route
                  path="/ai-agents-comparison"
                  element={<AIAgentsComparison />}
                />
                <Route
                  path="/agent-architecture-types"
                  element={<AgentArchitectureTypes />}
                />
                <Route
                  path="/agent-interaction-patterns"
                  element={<AgentInteractionPatterns />}
                />
                <Route
                  path="/ai-automation-studio"
                  element={<AIAutomationStudio />}
                />
                <Route path="/startups" element={<Startups />} />
                <Route path="/fipsr-ai-journey" element={<FipsrAIJourney />} />
                <Route path="/founder-profile" element={<FounderProfile />} />
                <Route path="/solutions" element={<Solutions />} />
                <Route path="/platform" element={<Platform />} />
                <Route path="/pricing" element={<Pricing />} />
                <Route path="/infrastructure" element={<Infrastructure />} />
                <Route
                  path="/multi-agent-architectures"
                  element={<MultiAgentArchitectures />}
                />
                <Route
                  path="/rag-systems-architecture"
                  element={<RAGSystemsArchitecture />}
                />
                <Route
                  path="/api-key-registration"
                  element={<APIKeyRegistration />}
                />
                <Route
                  path="/ai-agent-configuration"
                  element={<AIAgentConfiguration />}
                />
                <Route path="/use-cases" element={<UseCases />} />
                <Route path="/about" element={<About />} />
                <Route path="/contact" element={<Contact />} />
                <Route
                  path="/theme-designer"
                  element={<ThemeDesigner onChangeTheme={handleThemeChange} />}
                />
              </Routes>
            </Suspense>
            <Footer />
          </DndProvider>
        </ThemeProviderWrapper>
      </ResponsiveProvider>
    </Router>
  );
};

const mapStateToProps = (state) => ({
  cardDetails: state.libraryCards?.cardDetails || null,
});

const mapDispatchToProps = {
  getLibraryCardRequest,
};

export default connect(mapStateToProps, mapDispatchToProps)(App);
